/** @jsxImportSource theme-ui */
import { Badge, Box, Button, Checkbox, Flex } from 'theme-ui';
import { Popover } from '@component-controls/components';
import { useEffect, useState } from 'react';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { format } from 'date-fns';
import TableCell from '@/components/TableCell';
import { Company, CompanyColumnKey } from './types';
import { definitions } from '@/apitypes';
import Actions from './ActionsMenu';
import AddFeatureFlagModal from './AddFeatureFlagModal';
import { getCompanyStatusOption } from './utils';
import updateCompanySettings from '@/api/updateCompanySettings';

export interface ColumnAction {
    value: string;
    label: string;
    icon?: any;
    onClick?: (evt) => void;
}

export const createSelectionColumn = (
    selectedIds: Set<string>,
    onToggle: (company: Company) => void,
    onToggleAll: (companies: Company[]) => void,
    allCompanies: Company[]
) => ({
    id: 'selection',
    accessor: (row: Company) => row.Id,
    Header: () => {
        const allSelected = allCompanies.length > 0 && allCompanies.every((c) => c.Id && selectedIds.has(c.Id));
        const someSelected = !allSelected && allCompanies.some((c) => c.Id && selectedIds.has(c.Id));
        return (
            <Box
                onClick={() => onToggleAll(allCompanies)}
                sx={{ cursor: 'pointer', display: 'inline-flex', opacity: someSelected ? 0.5 : 1 }}
            >
                <Checkbox checked={allSelected} onChange={() => {}} sx={{ color: 'white', 'input:checked ~ &': { color: 'white' } }} />
            </Box>
        );
    },
    width: '48px',
    Cell: ({ row }: { row: { original: Company } }) => {
        const id = row.original.Id;
        if (!id) return null;
        return (
            <Box onClick={() => onToggle(row.original)} sx={{ cursor: 'pointer', display: 'inline-flex' }}>
                <Checkbox checked={selectedIds.has(id)} onChange={() => {}} />
            </Box>
        );
    },
});

export const createColumns = (keys: CompanyColumnKey[]) => {
    const columns = keys.map((columnKey) => {
        let width;
        if (columnKey === 'Id') {
            width = '200px';
        }
        if (columnKey === 'StatusId') {
            return {
                columnKey,
                Cell: ({
                    cell: { value },
                    row,
                }: {
                    cell: { value: any };
                    row: { original: Company };
                }) => {
                    const statusOption = getCompanyStatusOption(value);
                    const scheduledDeletionDate = row.original.ScheduledDeletionDate;
                    return (
                        <Flex sx={{ gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                            {statusOption ? (
                                <Badge backgroundColor={statusOption.color}>
                                    {statusOption.value}
                                </Badge>
                            ) : null}
                            {scheduledDeletionDate ? (
                                <Badge backgroundColor="danger" title="Scheduled for deletion">
                                    Deletion {format(new Date(scheduledDeletionDate), 'd MMM yyyy')}
                                </Badge>
                            ) : null}
                        </Flex>
                    );
                },
                Header: 'Status',
                accessor: columnKey,
                width: '200px',
            };
        }

        if (columnKey.includes('SuperAdminSettings')) {
            const field = columnKey.split('.')[1];
            return {
                columnKey,
                Cell: ({ cell: { value }, row }: { cell: { value: any }; row: { original: Company } }) => {
                    const [checked, setChecked] = useState(!!value);
                    const [loading, setLoading] = useState(false);

                    useEffect(() => {
                        setChecked(!!value);
                    }, [value]);

                    const handleClick = async () => {
                        if (loading) return;
                        const enabled = !checked;
                        const id = row.original.Id;
                        if (!id) return;
                        setChecked(enabled);
                        setLoading(true);
                        try {
                            await updateCompanySettings({ CompanyId: id, [field]: enabled });
                        } catch {
                            setChecked(!enabled);
                        } finally {
                            setLoading(false);
                        }
                    };

                    return (
                        <Box onClick={handleClick} sx={{ cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1, display: 'inline-flex' }}>
                            <Checkbox checked={checked} onChange={() => {}} />
                        </Box>
                    );
                },
                Header: field,
                accessor: columnKey,
            };
        }

        if (columnKey !== 'Active') {
            return {
                columnKey,
                Header: columnKey === 'LogoType' ? () => null : columnKey,
                accessor: columnKey,
                Cell: TableCell,
                width,
            };
        }

        return {
            columnKey,
            Header: columnKey,
            accessor: (row: any) => (row.Active ? 'Active' : 'Inactive'),
            width,
        };
    });

    return [
        ...columns,
        {
            Header: () => null,
            columnKey: 'Actions',
            accessor: 'Actions',
            width: '40px',
            Cell: ({ row }: { row: { original: Company } }) => {
                const [isOpen, setIsOpen] = useState(false);
                const [featureFlagCompany, setFeatureFlagCompany] = useState<{ id: string; name?: string } | null>(null);
                const toggleActions = () => setIsOpen(!isOpen);

                return (
                    <Flex
                        sx={{ justifyContent: 'center', position: 'relative' }}
                        onMouseLeave={() => setIsOpen(false)}
                    >
                        {featureFlagCompany && (
                            <AddFeatureFlagModal
                                companyId={featureFlagCompany.id}
                                companyName={featureFlagCompany.name}
                                onClose={() => setFeatureFlagCompany(null)}
                            />
                        )}
                        <Button variant="plain" onClick={toggleActions}>
                            <DotsHorizontalIcon width={16} height={16} />
                        </Button>
                        <Popover
                            arrowVisible={false}
                            placement="auto-start"
                            trigger="focus"
                            modifiers={[
                                {
                                    enabled: true,
                                    name: 'applyStyle',
                                    phase: 'write',
                                    fn({ state }) {
                                        state.styles = {
                                            popper: {
                                                ...state.styles.popper,
                                                zIndex: '20',
                                            },
                                        };
                                    },
                                },
                            ]}
                            tooltipShown={isOpen}
                            tooltip={() => (
                                <Actions
                                    {...row.original}
                                    onAddFeatureFlag={() => {
                                        setIsOpen(false);
                                        setFeatureFlagCompany({ id: row.original.Id!, name: row.original.Name });
                                    }}
                                />
                            )}
                        />
                    </Flex>
                );
            },
        },
    ];
};

export const addActionsToRow = (row: definitions['CompanyQueryResponse']) => {
    return { ...row, Actions: 'Actions' };
};
